import Link from 'next/link';
import { FC, useEffect, useState } from 'react';

import { AftercareTile } from 'components/aftercare/aftercare-tile';
import RenderBlocks from 'components/cms-blocks/render-blocks';
import Accordion from 'components/common/accordion';
import { BreadcrumbStructuredData } from 'components/common/breadcrumbs/structured-data';
import Button from 'components/common/button';
import Container from 'components/common/container';
import { Flow } from 'components/common/flow';
import { AftercareFullLogo } from 'components/common/icons';
import Image from 'components/common/image';
import ItemCard from 'components/common/item-card';
import Main from 'components/common/main';
import Panel from 'components/common/panel';
import Section from 'components/common/section';
import { Heading3, Heading5, VisuallyHidden } from 'components/common/text';
import { TickList } from 'components/common/tick-list';
import { useArticleCta } from 'utils/articles';
import { mobile, tablet } from 'utils/media';

import { ALPHABET } from './constants';
import * as St from './styles';
import { IProps } from './types';
import { groupGenera } from './utils';

const AftercareListing: FC<IProps> = ({ data: { page } }) => {
  const groupedGenera = groupGenera(page);
  const { watchCta, readCta } = useArticleCta();

  const [selectedLetter, setSelectedLetter] = useState(
    groupedGenera.find(({ genera }) => !!genera?.length)?.letter === ALPHABET[0]
      ? ALPHABET[0]
      : '',
  );

  const onHashChange = () => {
    setSelectedLetter(window.location.hash.slice(1, 2));
  };

  useEffect(() => {
    window.addEventListener('hashchange', onHashChange);

    if (window.location.hash) {
      onHashChange();
    }

    return () => {
      window.removeEventListener('hashchange', onHashChange);
    };
  });

  return (
    <>
      <BreadcrumbStructuredData
        crumbs={[
          {
            label: 'Aftercare',
          },
        ]}
      />
      <Main light>
        {page.heroImage && (
          <St.AftercareHero>
            <Container>
              {!!page.benefits?.length && <span />}
              <AftercareFullLogo />
              <TickList labels={page.benefits} />
            </Container>
            <Image
              src={page.heroImage}
              aspectRatio={{ [mobile]: 1.25 / 1, [tablet]: 1 / 3 }}
              alt=""
              sizes="100vw"
            />
            <VisuallyHidden as="h1">Sproutl Care</VisuallyHidden>
          </St.AftercareHero>
        )}

        <RenderBlocks blocks={page.blocks || []} />

        <Section bg="normal">
          <Container inset>
            <St.AlphabetNav>
              {groupedGenera.map(({ letter, genera }) => (
                <St.LetterLink
                  key={letter}
                  as={!!genera?.length ? 'a' : 'span'}
                  href={!!genera?.length ? `#${letter}` : undefined}
                  aria-label={
                    !!genera?.length
                      ? `Jump to plants starting with ${letter}`
                      : undefined
                  }
                  selected={letter === selectedLetter}
                >
                  <span>{letter}</span>
                </St.LetterLink>
              ))}
            </St.AlphabetNav>

            <Panel variant="white">
              <St.AlphabetList>
                {groupedGenera.map(({ letter, genera, articles, ctas }) => {
                  if (!genera?.length) return null;

                  return (
                    <li key={letter} id={letter}>
                      <Accordion title={letter} initiallyOpen>
                        {!!genera?.length && (
                          <ol>
                            {genera.map((genus) => (
                              <AftercareTile genus={genus} key={genus._id} />
                            ))}
                          </ol>
                        )}
                        {!!articles.length && (
                          <Flow size="m">
                            <Heading5 as="h2" highlight>
                              Related Articles
                            </Heading5>
                            {articles.map(
                              ({ article, alternativeImage, _id }) => {
                                const { category, title, slug, image } =
                                  article;

                                return (
                                  <ItemCard
                                    key={_id}
                                    title={title}
                                    subtitle={category?.name}
                                    image={alternativeImage || image}
                                    href={`/articles/${slug?.current}`}
                                    cta={article.isVideo ? watchCta : readCta}
                                    sidenote={article.readingTime}
                                    variant={
                                      article.isVideo
                                        ? 'transparent'
                                        : 'default'
                                    }
                                    landscape
                                  />
                                );
                              },
                            )}
                          </Flow>
                        )}
                      </Accordion>

                      {!!ctas.length && (
                        <>
                          {ctas.map(
                            ({
                              title,
                              alternativeImage,
                              colorScheme,
                              cta,
                              _id,
                            }) => {
                              return (
                                <St.AftercareCta
                                  key={_id}
                                  colorScheme={colorScheme}
                                >
                                  <Flow size="l-2xl">
                                    <Heading3 as="h3">{title}</Heading3>
                                    {cta?.title && cta.url && (
                                      <Link href={cta?.url} passHref>
                                        <Button
                                          variant="secondary"
                                          colorScheme={colorScheme || 'white'}
                                        >
                                          {cta?.title}
                                        </Button>
                                      </Link>
                                    )}
                                  </Flow>
                                  <Image
                                    src={alternativeImage}
                                    aspectRatio={0.8}
                                    sizes={`${mobile} 325px, 430px`}
                                    alt=""
                                    objectFit="cover"
                                  />
                                </St.AftercareCta>
                              );
                            },
                          )}
                        </>
                      )}
                    </li>
                  );
                })}
              </St.AlphabetList>
            </Panel>
          </Container>
        </Section>
      </Main>
    </>
  );
};

export default AftercareListing;
